import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, formatApiError } from "@/lib/api";
import { setActiveUid } from "@/lib/userScope";
import { startSync, stopSync, flushPendingSync } from "@/lib/cloudSync";
import { ignore } from "@/lib/log";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = still checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const t = getToken();
      if (!t) {
        if (active) setUser(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (active) {
          setActiveUid(data.user.id);
          setUser(data.user);
          startSync();
        }
      } catch {
        setToken(null);
        setActiveUid(null);
        if (active) setUser(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const applyAuth = (data) => {
    setToken(data.token);
    setActiveUid(data.user.id);
    setUser(data.user);
    startSync();
  };

  const login = useCallback(async (credentials) => {
    const { data } = await api.post("/auth/login", credentials);
    applyAuth(data);
    return data.user;
  }, []);

  const loginWithCode = useCallback(async (payload) => {
    const { data } = await api.post("/auth/login-with-code", payload);
    applyAuth(data);
    return data.user;
  }, []);

  const redeemCode = useCallback(async (code) => {
    const { data } = await api.post("/me/redeem-code", { code });
    setUser(data.user);
    return data.user;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      return null;
    }
  }, []);

  const forgotPassword = useCallback(async (recoveryEmail) => {
    const { data } = await api.post("/auth/forgot-password", { recoveryEmail });
    return data.message;
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    const { data } = await api.post("/auth/reset-password", { token, newPassword });
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    applyAuth(data);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await flushPendingSync();
    stopSync();
    try {
      await api.post("/auth/logout");
    } catch {
      ignore();
    }
    setToken(null);
    setActiveUid(null);
    setUser(false);
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.put("/me/profile", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const changePassword = useCallback(async (payload) => {
    await api.put("/me/password", payload);
  }, []);

  const saveStarters = useCallback(async (starters) => {
    const { data } = await api.put("/me/starters", starters);
    setUser(data.user);
    return data.user;
  }, []);

  const completeOnboarding = useCallback(async () => {
    try {
      const { data } = await api.post("/me/onboarding-complete");
      setUser(data.user);
      return data.user;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, login, loginWithCode, redeemCode, refreshUser, register, logout, forgotPassword, resetPassword, updateProfile, changePassword, saveStarters, completeOnboarding, formatApiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
